import type { CollectionEntityDefinition } from '@gzl10/nexus-sdk'
import { useIdField, useTextField, useSelectField, useDatetimeField, useExpiresAtField } from '@gzl10/nexus-sdk/fields'
import { revokeTokenAction } from './actions/revoke-token.action.js'

/**
 * EntityDefinition for Personal Access Tokens.
 *
 * Persistent tokens for API authentication.
 * Each user manages their own tokens (CASL self-access).
 */
export const personalTokenEntity: CollectionEntityDefinition = {
  type: 'collection',
  table: 'auth_personal_tokens',
  label: { en: 'Personal Access Token', es: 'Token de acceso personal' },
  labelPlural: { en: 'Personal Access Tokens', es: 'Tokens de acceso personal' },
  labelField: 'name',
  timestamps: true,
  hidden: false,
  allowCreate: false,
  allowEdit: false,
  allowDelete: false,
  order: 6,
  routePrefix: '/personal-tokens',

  actions: [revokeTokenAction],

  fields: {
    id: useIdField(),
    user_id: useSelectField({
      label: { en: 'User', es: 'Usuario' },
      table: 'users',
      column: 'id',
      onDelete: 'CASCADE',
      required: true,
      index: true,
      endpoint: '/users',
      valueField: 'id',
      labelField: 'name',
      meta: { searchable: true }
    }),
    name: useTextField({
      label: { en: 'Name', es: 'Nombre' },
      required: true,
      size: 100,
      nullable: false,
      hint: { en: 'Descriptive name for this token', es: 'Nombre descriptivo para este token' },
      meta: { searchable: true }
    }),
    token_prefix: useTextField({
      label: { en: 'Token', es: 'Token' },
      size: 20,
      disabled: true,
      nullable: false,
      hint: { en: 'Partial token for identification', es: 'Token parcial para identificación' }
    }),
    token_hash: useTextField({
      label: { en: 'Token Hash', es: 'Hash del token' },
      size: 64,
      hidden: true,
      nullable: false,
      unique: true,
      meta: { exportable: false }
    }),
    scope: useSelectField({
      label: { en: 'Permission', es: 'Permiso' },
      required: true,
      options: [
        { value: 'readonly', label: { en: 'Read-only', es: 'Solo lectura' } },
        { value: 'readwrite', label: { en: 'Read & Write', es: 'Lectura y escritura' } }
      ],
      meta: { sortable: true }
    }),
    expires_at: useExpiresAtField(),
    last_used_at: useDatetimeField({
      label: { en: 'Last Used', es: 'Último uso' },
      nullable: true,
      disabled: true,
      meta: { sortable: true }
    }),
  },

  indexes: [
    { columns: ['user_id'] }
  ],

  casl: {
    subject: 'AuthPersonalToken',
    permissions: {
      '@': [
        { actions: ['read', 'delete'], conditions: { user_id: '${user.id}' } }
      ]
    }
  }
}
